import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@repo/videa-components/src/index';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown, Mic } from 'lucide-react';

import {
  hasMicrophonePermissionAtom,
  isDeviceSelectionOpenAtom,
  isRecorderSheetOpenAtom,
  recorderStatusAtom,
} from '../../atoms';
import { useAudioDeviceManager } from '../permissions';

interface MicrophoneSelectorProps {
  onMonitoringStreamChange?: (stream: MediaStream | null) => void;
}

export const MicrophoneSelector = ({
  onMonitoringStreamChange,
}: MicrophoneSelectorProps) => {
  const [isOpen, setIsOpen] = useAtom(isDeviceSelectionOpenAtom);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const hasMicrophonePermission = useAtomValue(hasMicrophonePermissionAtom);
  const [monitoringStream, setMonitoringStream] = useState<MediaStream | null>(
    null,
  );
  const isSheetOpen = useAtomValue(isRecorderSheetOpenAtom);
  const recorderStatus = useAtomValue(recorderStatusAtom);

  const { availableDevices, selectedDeviceId, isLoading, error, selectDevice } =
    useAudioDeviceManager();

  const selectedDevice = availableDevices.find(
    (device) => device.deviceId === selectedDeviceId,
  );

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const monitoringStreamRef = useRef<MediaStream | null>(null);
  const onMonitoringStreamChangeRef = useRef(onMonitoringStreamChange);
  const prevDeviceIdRef = useRef<string | null>(null);

  const handleDeviceSelect = (deviceId: string) => {
    selectDevice(deviceId);
    setIsOpen(false);
  };

  const stopMicrophoneMonitoring = useCallback(() => {
    setIsMonitoring(false);

    const stream = monitoringStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setMonitoringStream(null);
      onMonitoringStreamChangeRef.current?.(null);
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
  }, []);

  const startMicrophoneMonitoring = useCallback(
    async (deviceId: string | null) => {
      try {
        setIsMonitoring(true);

        // Get audio stream with selected device
        const constraints = deviceId
          ? { audio: { deviceId: { exact: deviceId } } }
          : { audio: true };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        monitoringStreamRef.current = stream;
        setMonitoringStream(stream);
        onMonitoringStreamChangeRef.current?.(stream);

        // Set up audio analysis for the VoiceCard
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
      } catch (err) {
        console.error('Failed to start microphone monitoring:', err);
        setIsMonitoring(false);
      }
    },
    [],
  );

  // Keep refs in sync with their values
  useEffect(() => {
    monitoringStreamRef.current = monitoringStream;
  }, [monitoringStream]);

  useEffect(() => {
    onMonitoringStreamChangeRef.current = onMonitoringStreamChange;
  }, [onMonitoringStreamChange]);

  // Main effect: manage monitoring based on sheet state, device selection, and recording status
  useEffect(() => {
    // Only allow monitoring when recorder is idle (not recording, paused, stopped, etc.)
    const canMonitor = recorderStatus === 'idle';
    const deviceChanged =
      prevDeviceIdRef.current !== null &&
      prevDeviceIdRef.current !== selectedDeviceId;

    // Stop monitoring if: sheet closed or not in idle state
    if (!isSheetOpen || !canMonitor) {
      if (isMonitoring) {
        stopMicrophoneMonitoring();
      }
    }
    // Start/restart monitoring if: sheet open, idle state, device selected, not already monitoring
    // Also restart if device changed while sheet is open
    else if (selectedDeviceId && !isMonitoring) {
      startMicrophoneMonitoring(selectedDeviceId);
    } else if (deviceChanged && isMonitoring) {
      // Device changed while monitoring - restart with new device
      stopMicrophoneMonitoring();
      startMicrophoneMonitoring(selectedDeviceId);
    }

    prevDeviceIdRef.current = selectedDeviceId;
  }, [
    isSheetOpen,
    selectedDeviceId,
    recorderStatus,
    isMonitoring,
    startMicrophoneMonitoring,
    stopMicrophoneMonitoring,
  ]);

  // Safety net: clean up stream if it shouldn't exist
  // This handles edge cases where getUserMedia resolves after:
  // - Sheet is closed
  // - Recording has started or stopped (not idle)
  useEffect(() => {
    const canMonitor = recorderStatus === 'idle';
    const shouldNotHaveStream = !isSheetOpen || !canMonitor;

    if (shouldNotHaveStream && monitoringStream) {
      monitoringStream.getTracks().forEach((track) => track.stop());
      setMonitoringStream(null);
      onMonitoringStreamChangeRef.current?.(null);
    }
  }, [isSheetOpen, recorderStatus, monitoringStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMicrophoneMonitoring();
    };
  }, [stopMicrophoneMonitoring]);

  if (error) {
    return (
      <div className="vai-flex vai-items-center vai-gap-2 vai-p-2 vai-bg-red-50 vai-border vai-border-red-200 vai-rounded-md">
        <Mic className="vai-w-4 vai-h-4 vai-text-red-500" />
        <span className="vai-text-sm vai-text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div className="vai-space-y-3">
      <div className="vai-relative">
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading || availableDevices.length === 0}
          className="vai-flex vai-items-center vai-gap-2 vai-w-full vai-justify-between vai-font-normal"
        >
          <div className="vai-flex vai-items-center vai-gap-2 vai-min-w-0 vai-flex-1">
            <Mic className="vai-w-4 vai-h-4 vai-flex-shrink-0" />
            <span className="vai-text-sm vai-truncate vai-min-w-0">
              {!hasMicrophonePermission
                ? 'None'
                : isLoading
                  ? 'Loading microphones...'
                  : selectedDevice?.label || 'Select microphone'}
            </span>
          </div>
          <ChevronDown
            className={`vai-w-4 vai-h-4 vai-transition-transform ${isOpen ? 'vai-rotate-180' : ''}`}
          />
        </Button>

        {isOpen && (
          <div className="vai-absolute vai-top-full vai-left-0 vai-right-0 vai-mt-1 vai-bg-white vai-border vai-border-gray-200 vai-rounded-md vai-shadow-lg vai-z-50 vai-max-h-60 vai-overflow-y-auto">
            {!hasMicrophonePermission ? (
              <div className="vai-p-3 vai-text-sm vai-text-gray-500 vai-text-center">
                Microphone permission required
              </div>
            ) : availableDevices.length === 0 ? (
              <div className="vai-p-3 vai-text-sm vai-text-gray-500 vai-text-center">
                No microphones found
              </div>
            ) : (
              <>
                <div className="vai-p-2 vai-border-b vai-border-gray-200">
                  <span className="vai-text-sm">Select Microphone</span>
                </div>
                {availableDevices.map((device) => (
                  <button
                    key={device.deviceId}
                    onClick={() => handleDeviceSelect(device.deviceId)}
                    className={`vai-w-full vai-px-3 vai-py-2 vai-text-left vai-text-sm hover:vai-bg-gray-50 vai-flex vai-items-center vai-gap-2 ${
                      device.deviceId === selectedDeviceId
                        ? 'vai-bg-blue-50 vai-text-blue-700'
                        : 'vai-text-gray-700'
                    }`}
                  >
                    <Mic className="vai-w-4 vai-h-4 vai-flex-shrink-0" />
                    <span className="vai-truncate">{device.label}</span>
                    {device.deviceId === selectedDeviceId && (
                      <div className="vai-w-2 vai-h-2 vai-bg-blue-500 vai-rounded-full vai-ml-auto" />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
